import uniqBy from 'lodash.uniqby';

interface Identifiable {
  id?: string | null;
}

export interface RangeSelectEvent {
  shiftKey?: boolean;
}

const getItemsInRange = <T extends Identifiable>(
  items: T[],
  fromId: string,
  toId: string,
): T[] => {
  const fromIndex = items.findIndex(item => item.id === fromId);
  const toIndex = items.findIndex(item => item.id === toId);
  if (fromIndex === -1 || toIndex === -1) {
    return [];
  }
  const start = Math.min(fromIndex, toIndex);
  const end = Math.max(fromIndex, toIndex);
  return items.slice(start, end + 1);
};

interface ApplyRangeSelectionParams<T extends Identifiable> {
  displayedItems: T[];
  item: T;
  lastSelectedId?: string;
  selectedItems: T[];
  shiftKey: boolean;
}

const applyRangeSelection = <T extends Identifiable>({
  displayedItems,
  item,
  lastSelectedId,
  selectedItems,
  shiftKey,
}: ApplyRangeSelectionParams<T>): {
  lastSelectedId?: string;
  selectedItems: T[];
} => {
  if (shiftKey && lastSelectedId && item.id) {
    const range = getItemsInRange(displayedItems, lastSelectedId, item.id);
    if (range.length > 0) {
      return {
        lastSelectedId,
        selectedItems: uniqBy(
          [...selectedItems, ...range],
          selectedItem => selectedItem.id,
        ),
      };
    }
  }

  const isAlreadySelected = selectedItems.some(selectedItem => selectedItem.id === item.id);
  return {
    lastSelectedId: item.id ?? undefined,
    selectedItems: isAlreadySelected
      ? selectedItems.filter(selectedItem => selectedItem.id !== item.id)
      : [...selectedItems, item],
  };
};

export {
  applyRangeSelection,
  getItemsInRange,
};
